import React, { useState } from 'react';

/**
 * Toolbar component for document actions
 * 
 * @param {Object} props - Component props
 * @param {Object} props.document - Current document
 * @param {Function} props.onSave - Save handler
 * @param {Function} props.onUndo - Undo handler
 * @param {Function} props.onRedo - Redo handler
 * @param {Function} props.onTitleChange - Title change handler
 * @param {Function} props.onExportToOscar - Export to Oscar handler
 * @param {Function} props.onExportToWord - Export to Word handler
 * @param {boolean} props.canUndo - Whether undo is available
 * @param {boolean} props.canRedo - Whether redo is available
 * @returns {JSX.Element} - Rendered component
 */
function DocumentToolbar({ 
  document, 
  onSave, 
  onUndo, 
  onRedo, 
  onTitleChange,
  onExportToOscar,
  onExportToWord,
  canUndo = false, 
  canRedo = false 
}) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState(document?.title || '');
  
  const handleTitleClick = () => {
    setIsEditingTitle(true);
    setTitle(document?.title || '');
  };
  
  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (onTitleChange && title !== document?.title) {
      onTitleChange(title);
    }
  };
  
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      setTitle(document?.title || '');
      setIsEditingTitle(false);
    }
  };
  
  // If no document is active, show a disabled toolbar
  if (!document) {
    return (
      <div style={{ 
        padding: '6px 12px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        opacity: 0.6
      }}>
        <h3 style={{ margin: 0, fontSize: '14px' }}>No document selected</h3>
      </div>
    );
  }
  
  return (
    <div style={{ 
      padding: '6px 12px',
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        {/* Document title */}
        {isEditingTitle ? (
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            autoFocus
            style={{
              fontSize: '15px',
              border: '1px solid #0078d4',
              borderRadius: '4px',
              padding: '4px 6px',
              width: '250px'
            }}
          />
        ) : (
          <h3 
            onClick={handleTitleClick} 
            style={{ 
              margin: 0, 
              cursor: 'pointer',
              marginRight: '6px',
              padding: '3px 6px',
              borderRadius: '4px',
              border: '1px solid transparent',
              color: document.isDirty ? '#555' : '#333',
              fontSize: '15px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
              e.currentTarget.style.borderColor = '#ddd';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            {document.title}
            {document.isDirty && (
              <span style={{ marginLeft: '6px', fontSize: '13px', color: '#888' }}>•</span>
            )}
          </h3>
        )}
        
        {/* Color indicator */}
        <div style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: document.colorCode,
          marginLeft: '6px',
          border: '1px solid rgba(0,0,0,0.1)'
        }} />
      </div>
      
      <div style={{ display: 'flex', gap: '6px' }}>
        {/* Edit actions */}
        <div style={{ 
          display: 'flex', 
          borderRadius: '3px',
          overflow: 'hidden',
          border: '1px solid #d0d0d0',
          marginRight: '12px'
        }}>
          <button 
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo"
            style={{
              backgroundColor: canUndo ? 'white' : '#f8f8f8',
              border: 'none',
              borderRight: '1px solid #d0d0d0',
              padding: '3px 8px',
              cursor: canUndo ? 'pointer' : 'default',
              opacity: canUndo ? 1 : 0.6,
              fontSize: '13px'
            }}
          >
            ↩
          </button>
          <button 
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo"
            style={{
              backgroundColor: canRedo ? 'white' : '#f8f8f8',
              border: 'none',
              padding: '3px 8px',
              cursor: canRedo ? 'pointer' : 'default',
              opacity: canRedo ? 1 : 0.6,
              fontSize: '13px'
            }}
          >
            ↪
          </button>
        </div>
        
        {/* Save action */}
        <button 
          onClick={onSave}
          disabled={!document.isDirty}
          style={{
            backgroundColor: document.isDirty ? '#0078d4' : '#f0f0f0',
            color: document.isDirty ? 'white' : '#888',
            border: 'none',
            borderRadius: '3px',
            padding: '4px 10px',
            cursor: document.isDirty ? 'pointer' : 'default',
            fontSize: '13px'
          }}
        >
          Save
        </button>
        
        {/* Export actions */}
        <div style={{ position: 'relative' }}>
          <select
            style={{
              backgroundColor: 'white',
              border: '1px solid #d0d0d0',
              borderRadius: '3px',
              padding: '4px 10px',
              paddingRight: '22px',
              appearance: 'none',
              cursor: 'pointer',
              fontSize: '13px'
            }}
            defaultValue=""
            onChange={(e) => {
              const action = e.target.value;
              if (action === 'oscar') {
                onExportToOscar && onExportToOscar();
              } else if (action === 'word') {
                onExportToWord && onExportToWord();
              }
              e.target.value = ''; // Reset after selection
            }}
          >
            <option value="" disabled>Export</option>
            <option value="oscar">Export to Oscar (TXT)</option>
            <option value="word">Export to Word (DOCX)</option>
          </select>
          <div style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            fontSize: '10px'
          }}>
            ▼
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentToolbar;